import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categoryService } from '../services/categoryService';
import { getImageUrl } from '../utils/imageHelper';
import Loading from '../components/common/Loading';
import './Categories.css';

const Categories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await categoryService.getAllCategories({
        page: pagination.page,
        limit: pagination.limit,
      });
      
      setCategories(data.categories || []);
      setPagination((prev) => ({
        ...prev,
        ...data.pagination,
      }));
    } catch (err) {
      let errorMessage = 'Failed to load categories';
      if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleDelete = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete "${categoryName}"?`)) {
      return;
    }

    try {
      await categoryService.deleteCategory(categoryId);
      // Reload categories after deletion
      loadCategories();
    } catch (err) {
      const errorMsg = err.message || 'Unknown error';
      if (errorMsg.includes('associated products')) {
        alert(`Cannot delete category: ${errorMsg}`);
      } else {
        alert('Failed to delete category: ' + errorMsg);
      }
    }
  };


  if (loading) {
    return <Loading message="Loading categories..." />;
  }

  return (
    <div className="categories-container">
      <div className="page-header">
        <h1 className="page-title">Categories</h1>
        <Link to="/categories/create" className="btn-primary">
          Create Category
        </Link>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="categories-content">
        {categories.length === 0 ? (
          <div className="empty-state">
            <p>No categories found.</p>
            <Link to="/categories/create" className="btn-primary">
              Create Your First Category
            </Link>
          </div>
        ) : (
          <>
            <div className="categories-table-container">
              <table className="categories-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => {
                    const imageUrl = category.image_url ? getImageUrl(category.image_url) : null;
                    
                    return (
                      <tr key={category.id}>
                        <td>
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={category.name}
                              className="category-thumbnail"
                              onError={(e) => {
                                console.error('Image failed to load:', e.target.src);
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="no-image">No Image</div>
                          )}
                        </td>
                        <td>
                          <div className="category-name">{category.name}</div>
                        </td>
                        <td>
                          <code className="category-slug">{category.slug}</code>
                        </td>
                        <td>
                          {category.description ? (
                            <div className="category-description">
                              {category.description.length > 100
                                ? `${category.description.substring(0, 100)}...`
                                : category.description}
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => navigate(`/categories/edit/${category.id}`)}
                              className="btn-edit"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(category.id, category.name)}
                              className="btn-delete"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                  disabled={pagination.page === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </span>
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                  disabled={pagination.page >= pagination.pages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Categories;
